import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, UserX, UserCheck } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

export default function AdminTeam() {
  const { user, headers, logout } = useAdminAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "corretor", commission_share: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users`, { headers: headers() });
      setUsers(r.data);
    } catch {
      setUsers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.role === "corretor" && payload.commission_share !== "") {
        payload.commission_share = Number(String(payload.commission_share).replace(",", "."));
      } else {
        delete payload.commission_share;
      }
      await axios.post(`${API}/users`, payload, { headers: headers() });
      toast.success(`${form.role === "admin" ? "Admin" : form.role === "administrativo" ? "Administrativo" : "Corretor"} cadastrado!`);
      setForm({ name: "", email: "", password: "", role: "corretor", commission_share: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível cadastrar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    const action = u.active === false ? "reativar" : "desativar";
    if (!window.confirm(`Deseja ${action} ${u.name || u.email}?`)) return;
    try {
      await axios.put(`${API}/users/${u.email}`, { active: u.active === false }, { headers: headers() });
      toast.success(`Usuário ${u.active === false ? "reativado" : "desativado"}.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível atualizar.");
    }
  };

  const saveShare = async (u, value) => {
    const share = value.trim() === "" ? null : Number(value.replace(",", "."));
    if (share !== null && (isNaN(share) || share < 0 || share > 100)) {
      toast.error("Participação deve ser entre 0 e 100.");
      return;
    }
    if ((u.commission_share ?? null) === share) return;
    try {
      await axios.put(`${API}/users/${u.email}`, { commission_share: share ?? 50 }, { headers: headers() });
      toast.success(`Participação de ${u.name || u.email} salva.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar.");
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-team">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Equipe</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="team-title">
          Corretores e acesso
        </h1>
      </div>

      {!isAdmin ? (
        <p className="rounded-lg bg-white p-10 text-center font-semibold text-seed-deep/60 shadow-sm" data-testid="team-restricted">
          Acesso restrito ao administrador.
        </p>
      ) : (
        <>
          <form onSubmit={createUser} className="rounded-lg bg-white p-8 shadow-sm" data-testid="team-form">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <UserPlus size={18} className="text-seed-salad" /> Cadastrar membro da equipe
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <input required placeholder="Nome" value={form.name} onChange={set("name")} className={inputCls} data-testid="tf-name" />
              <input required type="email" placeholder="E-mail" value={form.email} onChange={set("email")} className={inputCls} data-testid="tf-email" />
              <input required type="text" minLength={6} placeholder="Senha inicial (mín. 6)" value={form.password} onChange={set("password")} className={inputCls} data-testid="tf-password" />
              <select value={form.role} onChange={set("role")} className={inputCls} data-testid="tf-role">
                <option value="corretor">Corretor</option>
                <option value="administrativo">Administrativo (agenda da equipe)</option>
                <option value="admin">Admin</option>
              </select>
              {form.role === "corretor" && (
                <div>
                  <input inputMode="decimal" placeholder="Participação na comissão % (ex.: 50)" value={form.commission_share} onChange={set("commission_share")} className={inputCls} data-testid="tf-commission-share" />
                  <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">% da comissão que fica com o corretor (padrão: 50%)</p>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="tf-submit"
              className="mt-5 rounded-full bg-seed-deep px-8 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {saving ? "Cadastrando..." : "Cadastrar"}
            </button>
          </form>

          <div className="mt-8 space-y-3" data-testid="team-list">
            {users === null ? (
              <div className="flex justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
              </div>
            ) : (
              users.map((u) => (
                <div
                  key={u.email}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-5 shadow-sm ${u.active === false ? "opacity-50" : ""}`}
                  data-testid={`team-item-${u.email}`}
                >
                  <div>
                    <p className="font-heading text-sm font-bold text-seed-deep">{u.name || "Sem nome"}</p>
                    <p className="text-xs text-seed-deep/50">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${u.role === "admin" ? "bg-seed-deep text-seed-off" : "bg-seed-salad/10 text-seed-salad"}`}>
                      {u.role === "admin" ? "Admin" : u.role === "administrativo" ? "Adm" : "Corretor"}
                    </span>
                    {u.role === "corretor" && (
                      <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">
                        Comissão
                        <input
                          key={`${u.email}-${u.commission_share ?? ""}`}
                          inputMode="decimal"
                          defaultValue={u.commission_share ?? ""}
                          placeholder="50"
                          onBlur={(e) => saveShare(u, e.target.value)}
                          className="h-8 w-16 rounded-md bg-seed-off px-2 text-center text-xs font-bold text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                          data-testid={`team-share-${u.email}`}
                        />
                        %
                      </label>
                    )}
                    {u.active === false && (
                      <span className="rounded-full bg-red-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-red-500">Desativado</span>
                    )}
                    {u.email !== user?.email && (
                      <button
                        onClick={() => toggleActive(u)}
                        data-testid={`team-toggle-${u.email}`}
                        className="flex items-center gap-1.5 rounded-full border border-seed-deep/15 px-4 py-2 text-xs font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad"
                      >
                        {u.active === false ? <><UserCheck size={13} /> Reativar</> : <><UserX size={13} /> Desativar</>}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </main>
  );
}
